import _ from "lodash";
import { useState } from "react";
import { useDCContext } from "@src/context/dcContext";
import { useIsMobileWidth, useIsTabletWidth } from "@src/utils/mediaQuery.js";
import { trackActionActivity } from "@src/utils/userActivityTracker.jsx";
import {
  CardContainer,
  HorizontalLayout,
  VerticalLayout,
  SavingsCard,
  HorizontalResponsiveLayout,
} from "@src/components/cards";
import { DropDown, Tooltip } from "@src/components/atoms";
import HelpingLogoMakeADifference from "./HelpingLogoMakeADifference.jsx";
import PersonalPeriodDonationTable from "./PersonalPeriodDonationTable.jsx";
import PersonalAllTimeDonationTags from "./PersonalAllTimeDonationTags.jsx";
import { useOperatorContext } from "@src/context/operatorContext.jsx";

/**
 * @param {{
 *   charity: {description: string, donationAmount: number, donationStatement: string, id: string, name: string, url: string},
 *   customerYearlyImpact: Array<{ totalDonations: number, totalFuelSaved: number, year: string, impactPerBvr: any[] }>,
 *   operatorPeriodImpact: Array<{ behaviourGroupCode: string, behaviourGroupId: string, donationTotal: number, goalTargetAchievedOperator: boolean, periodKey: string, sumSavingsKg: number }>,
 *   operatorYearlyImpact: Array<{ totalDonations: number, totalFuelSaved: number, year: string, impactPerBvr: any[] }>,
 * }} props
 */
const ImpactDonationSection = ({
  charity,
  customerYearlyImpact,
  operatorPeriodImpact,
  operatorYearlyImpact,
}) => {
  const { profile } = useOperatorContext();
  const { operator, customer, behaviours } = profile;
  const { industry } = customer;
  const customerCode = customer.customerCode.toLowerCase();
  const isMobile = useIsMobileWidth();
  const isTablet = useIsTabletWidth();
  const dc = useDCContext();

  const personalAnnualYearOptions = operatorYearlyImpact
    .map((o) => o.year)
    .map(Number);
  const [personalAnnualCurrentYear, setPersonalAnnualCurrentYear] = useState(
    Math.max(...personalAnnualYearOptions).toString(),
  );

  const personalAnnualCurrentStats = operatorYearlyImpact.find(
    (o) => o.year === personalAnnualCurrentYear,
  );
  const personalAnnualRaised = personalAnnualCurrentStats?.totalDonations;
  const personalAllTimeRaised = _.sumBy(
    operatorYearlyImpact,
    (o) => o.totalDonations,
  );
  const companyAllTimeRaised = _.sumBy(
    customerYearlyImpact,
    (o) => o.totalDonations,
  );

  const donationStatement = dc.existsDynamicReplacements(
    `charities__${customerCode}__impact_page_donation_statement`,
  )
    ? dc.getDynamicReplacement(
        `charities__${customerCode}__impact_page_donation_statement`,
      )
    : dc.getDynamicReplacement(
        "charities__default__impact_page_donation_statement",
      );

  return (
    <section className="w-full text-black text-center fade-in">
      <div
        className={`${isMobile || isTablet ? "flex flex-col justify-center items-center p-6" : "grid grid-cols-2 p-12"} gap-6`}
      >
        <VerticalLayout gap={4} layoutType={"flex-bottom"}>
          <CardContainer padding={6} shadow={false}>
            <div className="relative">
              <Tooltip
                direction="down-right"
                content={[
                  dc.getDynamicReplacement(
                    "captain__tooltip__impact_donations",
                  ),
                ]}
              />
            </div>
            <div className="space-y-4">
              <h4>
                {dc.getDynamicReplacement(
                  "captain__impact__operator_annual_title",
                  [],
                  "Personal Annual",
                  true,
                )}
              </h4>

              {/* Year Select */}
              <DropDown
                value={personalAnnualCurrentYear}
                options={personalAnnualYearOptions
                  .sort((a, b) => Number(b) - Number(a))
                  .map((y) => ({
                    value: y,
                    text: y.toString(),
                  }))}
                onChange={(newValue) => {
                  trackActionActivity("IMPACT_SWITCH_YEAR", {
                    year: newValue,
                    tab: "donations",
                  });
                  setPersonalAnnualCurrentYear(newValue);
                }}
                className={isMobile ? `px-3 mx-auto mb-2` : "w-32 mx-auto"}
              />

              <p>{donationStatement}</p>

              {/* Donation Table + Savings */}
              <HorizontalResponsiveLayout
                horizontal={{ ratio: [1, 1] }}
                vertical={{ className: "p-3" }}
                gap={4}
              >
                <PersonalPeriodDonationTable
                  currentYear={personalAnnualCurrentYear}
                  industry={industry}
                  customerBehaviours={behaviours}
                  operatorPeriodImpact={operatorPeriodImpact}
                />
                <SavingsCard
                  mode="donations"
                  industry={industry}
                  customerCode={customerCode}
                  value={personalAnnualRaised}
                />
              </HorizontalResponsiveLayout>
            </div>
          </CardContainer>

          {/* Charity description */}
          <CardContainer
            padding={8}
            bgColor={"teal-300"}
            shadow={false}
            flexibleHeight
          >
            {charity?.description}
          </CardContainer>
        </VerticalLayout>

        {/* SECOND COLUMN */}
        <VerticalLayout gap={4}>
          <CardContainer padding={6} shadow={false}>
            <h4>
              {dc.getDynamicReplacement(
                "captain__impact__operator_alltime_title",
                [],
                "Personal All-time",
              )}
            </h4>
            <HorizontalResponsiveLayout
              horizontal={{ ratio: [1, 1] }}
              vertical={{ className: "p-3" }}
              gap={4}
            >
              <PersonalAllTimeDonationTags
                operatorPeriodImpact={operatorPeriodImpact}
                customerBehaviours={behaviours}
              />
              <SavingsCard
                mode="donations"
                industry={industry}
                customerCode={customerCode}
                value={personalAllTimeRaised}
              />
            </HorizontalResponsiveLayout>
          </CardContainer>

          <CardContainer padding={6} shadow={false}>
            <div className="flex flex-col gap-2">
              <h4>
                {dc.getDynamicReplacement(
                  "captain__impact__customer_alltime_title",
                  [],
                  "Company All-time",
                )}
              </h4>
              <SavingsCard
                mode="donations"
                industry={industry}
                customerCode={customerCode}
                value={companyAllTimeRaised}
                layout="horizontal"
              />

              <HelpingLogoMakeADifference customer={customer} />
            </div>
          </CardContainer>
        </VerticalLayout>
      </div>
    </section>
  );
};

export default ImpactDonationSection;
